import { CommonModule, DatePipe } from "@angular/common";
import { Component, inject } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatDialogModule } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import swal from "sweetalert2";

import {
  DatosDetalleIso,
  DetalleIncidenciaIsoBase,
  TextosDetalleIso,
} from "../../shared/incidencias-iso/detalle-incidencia-iso-base";
import { EstadoSiguienteIso, etiquetaOpcion, porquesDetalleIso } from "../../shared/incidencias-iso/incidencias-iso.comun";
import {
  EstadoIncidenciaIA,
  IncidenciaIA,
  IncidenciaIAPorques,
} from "../../shared/model/iso42001/incidencia-ia.model";
import {
  CARGO_SEGUIMIENTO_OPTIONS,
  GRAVEDAD_OPTIONS,
  INCIDENCIA_IA_COLUMNS,
  PRIORIDAD_OPTIONS,
  TIPO_EVENTO_OPTIONS,
} from "../incidencias-ia.constants";
import { IncidenciasIAService } from "../incidencias-ia.service";

export type DetalleIncidenciaIAData = DatosDetalleIso<IncidenciaIA>;

@Component({
  selector: "app-detalle-incidencia-ia",
  standalone: true,
  templateUrl: "./detalle-incidencia-ia.component.html",
  styleUrls: ["./detalle-incidencia-ia.component.css"],
  imports: [CommonModule, DatePipe, MatButtonModule, MatDialogModule, MatIconModule],
})
export class DetalleIncidenciaIAComponent extends DetalleIncidenciaIsoBase<IncidenciaIA> {
  readonly tipoEventoOptions = TIPO_EVENTO_OPTIONS;
  readonly gravedadOptions = GRAVEDAD_OPTIONS;
  readonly prioridadOptions = PRIORIDAD_OPTIONS;
  readonly cargoSeguimientoOptions = CARGO_SEGUIMIENTO_OPTIONS;

  protected readonly incidenciasService = inject(IncidenciasIAService);
  protected readonly columnasEstado = INCIDENCIA_IA_COLUMNS;
  protected readonly perfilAsignador = "Lider tecnico IA";
  protected readonly reabrirSeccionAlCargar = false;
  protected readonly textos: TextosDetalleIso = {
    sinIdentificador: "No se encontró el identificador de la incidencia IA.",
    errorCarga: "No fue posible cargar la incidencia IA.",
    noEncontrada: "No se encontró la incidencia IA.",
    soloAsignador: "Solo el Lider tecnico IA puede asignar una incidencia a proceso.",
    soloRevisor: "Solo el usuario que registro la incidencia puede confirmar Vo.Bo.",
    soloResponsable: "Solo el responsable asignado puede continuar esta incidencia.",
    servicioNoEncontrado:
      "El backend no encontro el servicio de detalle de incidencias IA. Revisa que CosRest este compilado y reiniciado.",
    respuestaNoJson: "El backend respondio una pagina en lugar de JSON. La ruta de detalle no esta llegando al controller.",
  };

  editar(): void {
    if (!this.incidencia?.idIncidenciaIa || !this.puedeEditar()) {
      return;
    }
    this.dialogRef.close({
      action: "edicion",
      refresh: this.requiereRefresh,
      incidencia: this.incidencia,
    });
  }

  abrirSeguimiento(): void {
    if (!this.incidencia?.idIncidenciaIa) {
      return;
    }
    this.dialogRef.close({
      action: "seguimiento",
      refresh: this.requiereRefresh,
      incidencia: this.incidencia,
    });
  }

  cerrarDetalle(): void {
    this.dialogRef.close({ refresh: true });
  }

  regresarAProceso(): void {
    if (!this.incidencia?.idIncidenciaIa || !this.puedeRegresarAProceso()) {
      this.error = "Solo el usuario que registro la incidencia puede regresarla a proceso.";
      return;
    }
    swal.fire({
      title: "Regresar a proceso",
      input: "textarea",
      inputLabel: "Comentario",
      inputPlaceholder: "Describe el motivo para regresar la incidencia a proceso.",
      inputAttributes: {
        maxlength: "500",
      },
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Guardar y regresar",
      cancelButtonText: "Cancelar",
      allowOutsideClick: true,
      inputValidator: (value) => {
        if (!value?.trim()) {
          return "Agrega un comentario antes de regresar a proceso.";
        }
        return null;
      },
    }).then((result) => {
      if (!result.isConfirmed || !result.value?.trim()) {
        return;
      }
      this.guardarComentarioYRegresarAProceso(result.value.trim());
    });
  }

  puedeEditar(): boolean {
    if (!this.incidencia || this.incidencia.estadoEvento === "VOBO") {
      return false;
    }
    if (this.incidencia.estadoEvento === "PROCESO") {
      return this.incidencia.idUsuarioResponsableSeguimiento === this.idUsuarioSesion;
    }
    if (this.incidencia.estadoEvento === "ATENDIDO") {
      return this.esUsuarioAlta() || this.incidencia.idUsuarioResponsableSeguimiento === this.idUsuarioSesion;
    }
    return true;
  }

  puedeCambiarEstado(): boolean {
    if (!this.incidencia || !this.siguienteEstado()) {
      return false;
    }
    if (this.incidencia.estadoEvento === "ABIERTO") {
      return this.esAsignador;
    }
    if (this.incidencia.estadoEvento === "PROCESO") {
      return this.incidencia.idUsuarioResponsableSeguimiento === this.idUsuarioSesion;
    }
    if (this.incidencia.estadoEvento === "ATENDIDO") {
      return this.esUsuarioAlta();
    }
    return false;
  }

  puedeRegresarAProceso(): boolean {
    return this.incidencia?.estadoEvento === "ATENDIDO" && this.esUsuarioAlta();
  }

  labelTipoEvento(value?: string): string {
    return etiquetaOpcion(value, this.tipoEventoOptions);
  }

  labelCargoSeguimiento(value?: string): string {
    return etiquetaOpcion(value, this.cargoSeguimientoOptions);
  }

  /** IA recarga el detalle después de cambiar el estado. */
  protected enviarCambioEstado(siguiente: EstadoSiguienteIso): void {
    this.solicitarCambioEstado(siguiente, undefined, () => this.cargar());
  }

  protected seccionDefaultPorEstado(estado?: EstadoIncidenciaIA): string {
    if (estado === "PROCESO" || estado === "ATENDIDO") {
      return "seguimiento";
    }
    if (estado === "VOBO") {
      return "";
    }
    return "resumen";
  }

  protected faltantesParaAtendido(): string[] {
    const faltantes: string[] = [];
    if (!this.incidencia?.fechaPlaneadaResolucion) {
      faltantes.push("Fecha planeada de resolución");
    }
    if (!this.incidencia?.accionCorrectiva?.trim()) {
      faltantes.push("Acción correctiva");
    }
    if (!this.incidencia?.validacionHumanaRealizada?.trim()) {
      faltantes.push("Validación humana realizada");
    }
    const porques = porquesDetalleIso(this.incidencia?.causaOrigen);
    for (let i = 1; i <= 5; i++) {
      if (!porques[String(i)]?.trim()) {
        faltantes.push(`Causa / Origen: porqué ${i}`);
      }
    }
    if (!this.comentarios.length) {
      faltantes.push("Al menos un comentario de observación");
    }
    return faltantes;
  }

  protected preguntaPorque(numero: number, porques: IncidenciaIAPorques): string {
    if (numero === 1) {
      return `¿Por qué fue ${this.labelTipoEvento(this.incidencia?.tipoEvento) || "el evento"}?`;
    }
    const anterior = this.quitarPrefijoPorque(porques[String(numero - 1)]) || `la respuesta ${numero - 1}`;
    return `¿Por qué ${anterior}?`;
  }

  private quitarPrefijoPorque(value?: string): string {
    return (value || "")
      .trim()
      .replace(/^(por\s+que|por\s+qué|porque)\s+/i, "")
      .trim();
  }
}
